import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supportService } from '../../services/supportService';
import { TicketStatus, SupportTicketDetail } from '../../types/support';

const statusColor = (s: TicketStatus) => {
  switch (s) {
    case TicketStatus.OPEN: return 'warning';
    case TicketStatus.IN_PROGRESS: return 'info';
    case TicketStatus.RESOLVED: return 'success';
    case TicketStatus.CLOSED: return 'default';
  }
};

export const AdminSupportPage = () => {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reply, setReply] = useState('');

  const { data: tickets, isLoading } = useQuery({
    queryKey: ['admin-support-tickets', statusFilter],
    queryFn: () => supportService.adminListTickets(statusFilter || undefined),
  });

  const { data: ticketDetail, isLoading: loadingDetail, refetch } = useQuery({
    queryKey: ['admin-support-ticket', selectedId],
    queryFn: () => supportService.adminGetTicket(selectedId!),
    enabled: !!selectedId,
  });

  const replyMutation = useMutation({
    mutationFn: () => supportService.adminReply(selectedId!, reply),
    onSuccess: () => {
      setReply('');
      refetch();
      qc.invalidateQueries({ queryKey: ['admin-support-tickets'] });
    },
  });

  const statusMutation = useMutation({
    mutationFn: (status: TicketStatus) => supportService.adminUpdateStatus(selectedId!, status),
    onSuccess: () => {
      refetch();
      qc.invalidateQueries({ queryKey: ['admin-support-tickets'] });
    },
  });

  if (selectedId && ticketDetail) {
    return (
      <Box sx={{ p: 4, maxWidth: 860 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => setSelectedId(null)} sx={{ mb: 2 }}>
          {t('common.back')}
        </Button>

        <Paper sx={{ p: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>{ticketDetail.subject}</Typography>
              <Typography variant="caption" color="text.secondary">
                {ticketDetail.user_name} ({ticketDetail.user_email}) &middot;{' '}
                {t(`support.categories.${ticketDetail.category}`)} &middot;{' '}
                {new Date(ticketDetail.created_at).toLocaleDateString('fr-FR')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip label={t(`support.statuses.${ticketDetail.status}`)} color={statusColor(ticketDetail.status)} />
              <Select
                size="small"
                value={ticketDetail.status}
                onChange={(e) => statusMutation.mutate(e.target.value as TicketStatus)}
                sx={{ minWidth: 140 }}
                disabled={statusMutation.isPending}
              >
                {Object.values(TicketStatus).map((s) => (
                  <MenuItem key={s} value={s}>{t(`support.statuses.${s}`)}</MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </Paper>

        {loadingDetail && <CircularProgress />}

        <Box sx={{ mb: 2 }}>
          {ticketDetail.messages?.map((msg) => (
            <Box
              key={msg.id}
              sx={{
                display: 'flex',
                justifyContent: msg.is_admin_reply ? 'flex-end' : 'flex-start',
                mb: 1.5,
              }}
            >
              <Paper
                sx={{
                  p: 2,
                  maxWidth: '75%',
                  bgcolor: msg.is_admin_reply ? 'primary.light' : 'grey.100',
                  borderRadius: 2,
                }}
              >
                <Typography variant="caption" fontWeight={600} color={msg.is_admin_reply ? 'primary.dark' : 'text.secondary'}>
                  {msg.is_admin_reply ? t('support.supportTeam') : (msg.sender_name ?? msg.sender_id)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {new Date(msg.created_at).toLocaleString('fr-FR')}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>

        <Paper sx={{ p: 2 }}>
          <TextField
            placeholder={t('support.replyPlaceholder')}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            fullWidth
            multiline
            rows={4}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              endIcon={<SendIcon />}
              onClick={() => replyMutation.mutate()}
              disabled={!reply.trim() || replyMutation.isPending}
            >
              {t('support.send')}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t('support.adminTitle')}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t('common.status')}</InputLabel>
          <Select
            value={statusFilter}
            label={t('common.status')}
            onChange={(e) => setStatusFilter(e.target.value as TicketStatus | '')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {Object.values(TicketStatus).map((s) => (
              <MenuItem key={s} value={s}>{t(`support.statuses.${s}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {isLoading && <CircularProgress />}

      {!isLoading && (!tickets || tickets.length === 0) && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('support.noTickets')}</Typography>
        </Paper>
      )}

      {tickets?.map((ticket) => (
        <Paper
          key={ticket.id}
          sx={{ p: 2, mb: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          onClick={() => setSelectedId(ticket.id)}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography fontWeight={600}>{ticket.subject}</Typography>
              <Typography variant="caption" color="text.secondary">
                {ticket.user_name} ({ticket.user_email}) &middot;{' '}
                {t(`support.categories.${ticket.category}`)} &middot;{' '}
                {new Date(ticket.updated_at).toLocaleDateString('fr-FR')}
                {ticket.message_count > 0 && ` · ${ticket.message_count} msg`}
              </Typography>
            </Box>
            <Chip label={t(`support.statuses.${ticket.status}`)} color={statusColor(ticket.status)} size="small" />
          </Box>
        </Paper>
      ))}
    </Box>
  );
};
